import React, { memo, useCallback, useMemo } from 'react';
import '../../styles/svgPickerModal.css';

export interface SvgPickerModalProps {
  /** 可选的 SVG 列表 */
  readonly svgList: readonly string[];
  /** 选中某个 SVG */
  readonly onPick: (svgContent: string) => void;
  /** 关闭弹窗 */
  readonly onClose: () => void;
}

/**
 * SVG 缩略图卡片
 */
const SvgThumbnail: React.FC<{
  readonly svg: string;
  readonly index: number;
  readonly onPick: (svg: string) => void;
}> = memo(({ svg, index, onPick }) => {
  const handleClick = useCallback((): void => {
    onPick(svg);
  }, [svg, onPick]);

  // 为缩略图生成安全的内联 HTML（已经过 sanitize）
  const thumbnailHtml = useMemo(() => {
    // 对缩略图再做一次安全清洗
    let safe = svg.replace(/<script[\s\S]*?<\/script>/gi, '');
    safe = safe.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '');
    safe = safe.replace(/javascript:/gi, '');
    return safe;
  }, [svg]);

  return (
    <button
      type="button"
      className="svg-picker-card"
      onClick={handleClick}
      title={`选择 SVG #${index + 1}`}
    >
      <div
        className="svg-picker-thumbnail"
        dangerouslySetInnerHTML={{ __html: thumbnailHtml }}
      />
      <span className="svg-picker-label">SVG #{index + 1}</span>
    </button>
  );
});

SvgThumbnail.displayName = 'SvgThumbnail';

/**
 * SVG 选择弹窗
 * 当消息中包含多个 SVG 时，让用户选择要应用到画布的那个
 */
export const SvgPickerModal: React.FC<SvgPickerModalProps> = memo(({ svgList, onPick, onClose }) => {
  const handleOverlayClick = useCallback((e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  return (
    <div className="svg-picker-overlay" onClick={handleOverlayClick}>
      <div className="svg-picker-modal">
        <div className="svg-picker-header">
          <h3 className="svg-picker-title">选择要应用的 SVG</h3>
          <button
            type="button"
            className="svg-picker-close"
            onClick={onClose}
            title="关闭"
          >
            ✕
          </button>
        </div>
        <div className="svg-picker-grid">
          {svgList.map((svg, index) => (
            <SvgThumbnail
              key={`svg-pick-${index}`}
              svg={svg}
              index={index}
              onPick={onPick}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

SvgPickerModal.displayName = 'SvgPickerModal';